// src/firebase-config.js

import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getDatabase } from "firebase/database";
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { getRemoteConfig, fetchAndActivate, getValue } from "firebase/remote-config";

// ======================== ENV VAR VALIDATION ========================
// [SECURITY] Fail fast with descriptive error if required config is missing.
const REQUIRED_ENV_VARS = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_APP_ID',
];

const missingVars = REQUIRED_ENV_VARS.filter(key => !process.env[key]);
if (missingVars.length > 0) {
    const message = `[Firebase Config] Missing required environment variables:\n${missingVars.map(v => `  - ${v}`).join('\n')}\nPlease check your .env file.`;
    if (process.env.NODE_ENV === 'production') {
        // In production, log error but don't expose variable names to console
        console.error('[Firebase Config] Critical: Missing required environment variables. App may not function correctly.');
    } else {
        console.error(message);
    }
}

// Firebase configuration from environment variables
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize App Check with reCAPTCHA v3.
// [DEV] In development, use a debug token instead of real reCAPTCHA
// which doesn't work on localhost. The debug token is printed to the
// browser console on first run — register it in Firebase Console →
// App Check → Apps → Manage debug tokens.
const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
if (recaptchaSiteKey && recaptchaSiteKey.length > 10) {
    // Enable debug token in development (MUST be set BEFORE initializeAppCheck)
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-restricted-globals
        self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
    try {
        initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(recaptchaSiteKey),
            isTokenAutoRefreshEnabled: true
        });
    } catch (e) {
        console.warn('[Firebase] App Check initialization failed:', e.message);
    }
} else if (process.env.NODE_ENV !== 'production') {
    console.info('[Firebase] App Check skipped — REACT_APP_RECAPTCHA_SITE_KEY not set.');
}

// Initialize Firebase services
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const rtdb = getDatabase(app);

// ======================== EMULATOR GUARD ========================
// [SECURITY] Only connect to emulators in development. Prevents accidental
// emulator connections in production builds.
if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_EMULATORS === 'true') {
    try {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
        connectFirestoreEmulator(db, 'localhost', 8080);
        console.info('[Firebase] Connected to local emulators (Auth: 9099, Firestore: 8080)');
    } catch (e) {
        console.warn('[Firebase] Failed to connect to emulators:', e.message);
    }
}

// Initialize Analytics (conditionally — respects browser settings)
let analytics = null;
isAnalyticsSupported().then(supported => {
    if (supported) {
        analytics = getAnalytics(app);
    }
}).catch(() => { });

// Initialize Remote Config with defaults
const remoteConfig = getRemoteConfig(app);
// [SECURITY] Reduced from 1h to 15min for faster kill-switch propagation
// (e.g., disabling payments, blocking signups via remote config)
remoteConfig.settings.minimumFetchIntervalMillis = 900000; // 15 minute cache
remoteConfig.defaultConfig = {
    allow_new_google_signup: false,
    demo_instance_enabled: false,
    hdfc_backend_base_url: '',
    health_check_endpoints: '',
};

/**
 * Fetches and activates remote config values.
 * Returns an object with the config values.
 */
const fetchRemoteConfig = async () => {
    try {
        await fetchAndActivate(remoteConfig);
        const endpointsCsv = getValue(remoteConfig, 'health_check_endpoints').asString();
        return {
            allowNewGoogleSignup: getValue(remoteConfig, 'allow_new_google_signup').asBoolean(),
            demoInstanceEnabled: getValue(remoteConfig, 'demo_instance_enabled').asBoolean(),
            hdfcBackendBaseUrl: getValue(remoteConfig, 'hdfc_backend_base_url').asString(),
            healthCheckEndpoints: endpointsCsv.split(',').map(e => e.trim()).filter(e => e.length > 0),
        };
    } catch (error) {
        console.error("Remote Config fetch failed, using defaults:", error);
        return {
            allowNewGoogleSignup: false,
            demoInstanceEnabled: false,
            hdfcBackendBaseUrl: '',
            healthCheckEndpoints: [],
        };
    }
};

// Export the initialized services
export { app, auth, db, rtdb, storage, analytics, remoteConfig, fetchRemoteConfig };
